// Southern Methodist University: public college page (#2740), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - SMU's Common Data Set 2025-2026 (University Decision Support), which SMU
//    posts as one PDF per section; this page uses Part C, First-Time,
//    First-Year Admission: C1 applicants, admits and enrollees for Fall 2025
//    with the residency table, C8 test policy for Fall 2027 and the score
//    date, C21/C22 early plans. Its early decision counts are labelled for
//    the Fall 2024 entering class, so they are left out.
//  - smu.edu admission: First-Year application process (the Fall 2027
//    deadlines, dated November 1, 2026 and January 15, 2027), Early Decision
//    (binding terms and the early decision notification dates) and
//    Undergraduate testing (the test-optional policy; the page carries no
//    year label, it is the page as posted for the cycle open now).
//  - Student Financial Services, Cost of Attendance: the Estimated
//    Undergraduate Cost of Attendance for 2026-2027, on campus. The CDS
//    Part G gives the same 2026-2027 tuition, fees and food and housing;
//    the Student Financial Services page is used because it itemizes the
//    full budget with its total.
//  - College Scorecard API, school id 228246 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'SMU Common Data Set 2025-2026, Part C (First-Time, First-Year Admission)',
        url: 'https://www.smu.edu/-/media/site/ir/commondatasets/2025/cds-2025-26-part-c-first-time-freshman-final.pdf',
        read: '2026-10-01',
    },
    process: {
        label: 'SMU Undergraduate Admission, First-Year application process',
        url: 'https://www.smu.edu/admission/apply/undergraduate-admission/process',
        read: '2026-10-01',
    },
    earlyDecision: {
        label: 'SMU Undergraduate Admission, Early Decision',
        url: 'https://www.smu.edu/admission/apply/undergraduate-admission/early-decision',
        read: '2026-10-01',
    },
    testing: {
        label: 'SMU Undergraduate Admission, Undergraduate testing',
        url: 'https://www.smu.edu/admission/apply/undergraduate-admission/testing',
        read: '2026-10-01',
    },
    coa: {
        label: 'SMU Student Financial Services, Cost of Attendance 2026-2027',
        url: 'https://www.smu.edu/enrollment-services/student-financial-services/cost-and-payment/cost-of-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?228246-Southern-Methodist-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'southern-methodist-university',
    kbId: 'southern_methodist_university',
    name: 'Southern Methodist University',
    shortName: 'SMU',
    location: 'Dallas, TX',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (SMU brand color palette); decoration only.
    branding: {
        primary: '#CC0035',
        secondary: '#354CA1',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/southern_methodist_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 24053,
        admitted: 11459,
        enrolled: 1715,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 12254, admitted: 5071 },
            { group: 'Out-of-state', applied: 9805, admitted: 5859 },
            { group: 'International', applied: 1767, admitted: 437 },
            { group: 'Unknown', applied: 227, admitted: 92 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'SMU\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'The Common Data Set labels its early decision counts as fall 2024 figures and gives no early action counts, so it gives no early acceptance rate for fall 2025.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'Mid-December', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: null, binding: false },
            { plan: 'Early Decision II', deadline: 'January 15', decision: 'By March 1', binding: true },
            { plan: 'Regular Decision', deadline: 'January 15', decision: null, binding: false },
        ],
        note: 'You apply through the Common Application, ApplyTexas or the Coalition Application with Scoir. The early decision notification dates are from SMU\'s Early Decision page; SMU\'s admission pages publish no Early Action or Regular Decision notification date, and its Common Data Set 2025-2026 lists December 31 for Early Action and April 1 for Regular Decision. Applicants in art, film (BFA), dance, music or theater also have audition or portfolio requirements, with their own deadlines.',
        source: 'process',
        binding: 'Early Decision I and II are binding and differ only in their dates: you send the signed Early Decision Agreement Form, and if admitted you withdraw your other applications, pay the nonrefundable $685 deposit and attend SMU, provided you are offered sufficient financial assistance. Early Action is not binding; you have until May 1.',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'SMU is fully test-optional: no preference is given to applicants who submit or do not submit SAT or ACT scores, and applying without scores does not affect merit scholarship consideration. SMU superscores the SAT and ACT.',
        source: 'testing',
        cdsLine: 'The Common Data Set marks the SAT or ACT as considered if submitted, not required, for fall 2027, with scores due by March 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'testing',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition (12 to 18 hours a term)', amount: 63736 },
            { item: 'General student fees', amount: 8080 },
            { item: 'Housing and food (on campus)', amount: 20950 },
            { item: 'Books, supplies, loan fees', amount: 888 },
            { item: 'Transportation', amount: 1134 },
            { item: 'Personal and miscellaneous', amount: 1600 },
        ],
        total: 96388,
        note: 'SMU\'s estimated cost of attendance for a full-time undergraduate living on campus for the fall and spring terms. Summer and intersession are not included. Living off campus the estimate is $86,774, and with parents $79,660.',
        source: 'coa',
        netPrice: {
            average: 39816,
            byIncome: [
                { income: '$0 to $30,000', amount: 31728 },
                { income: '$30,001 to $48,000', amount: 18708 },
                { income: '$48,001 to $75,000', amount: 23206 },
                { income: '$75,001 to $110,000', amount: 28047 },
                { income: 'Over $110,000', amount: 57328 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from SMU\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
